import './app.scss';
import domready from 'domready';
import 'lazysizes';
import { loadLazyImages } from './utilities/js/lazy-image-loader';
import { bindTrackingEvent, sendTracking } from './utilities/js/tracking';
import { EventScroller } from './utilities/js/event-scroller';
import { getOffset, smoothScrollTo } from './utilities/js/helper';
import { Accessibility } from './layout/accessibility/accessibility';
import { ResizeHandler } from './utilities/js/resize-handler';

import AdjustSmartBanner from '@adjustcom/smart-banner-sdk';

// import libs and components

// GLOBAL OPTIONS
const OPTIONS = {
    breakpoints: {
        s: 320,
        m: 768,
        l: 1024,
        xl: 1280,
        xxl: 1440,
        xxxl: 1920
    },
    loader: '<b class="loading"><i>loading...</i></b>'
};

window.OPTIONS = OPTIONS;
window.eventScroller = new EventScroller();
window.resizeHandler = new ResizeHandler();

const browserCheck = () => {
    window.browser = {
        csshover: !window.matchMedia('(pointer:coarse)').matches,
        ios: (/iPhone|iPad|iPod/i).test(navigator.userAgent),
        iphone: (/iPhone|iPod/i).test(navigator.userAgent),
        android: (/(android)/i).test(navigator.userAgent),
        firefox: (/firefox/i).test(navigator.userAgent),
        safari: (/Version\/[\d.]+.*Safari/).test(navigator.userAgent)
    };

    Object.entries(window.browser).forEach(([key, value]) => {
        document.documentElement.classList.add((!value) ? `no-${key}` : `${key}`);
    });
    document.documentElement.classList.remove('no-js');
    document.documentElement.classList.add('js');
};

/**
 * define function for initialising modules
 */

const initContent = ($content) => {
    browserCheck();
    bindTrackingEvent($content);
    loadLazyImages();

    const $$rteButton = $content.querySelectorAll('.rte a');
    if ($$rteButton.length > 0) {
        for (let i = 0; i < $$rteButton.length; i++) {
            const $rteButton = $$rteButton[i];

            if (!$rteButton.getAttribute('data-tracking')) {
                let trackingData = null;
                let buttonType = 'primary';

                if ($rteButton.classList.contains('is--secondary')) {
                    buttonType = 'secondary';
                }

                if ($rteButton.classList.contains('download')) {
                    const file = $rteButton.href;
                    const fileSplitted = file.split('/');
                    const fileName = fileSplitted[fileSplitted.length - 1];

                    trackingData = {
                        event: 'ce.download_file',
                        elementText: fileName
                    };
                } else if ($rteButton.classList.contains('button')) {
                    trackingData = {
                        event: 'ce.cta_click',
                        elementText: $rteButton.innerText,
                        elementType: buttonType
                    };
                }

                $rteButton.addEventListener('click', () => {
                    sendTracking(trackingData);
                });
            }
        }
    }

    // Accessibility
    const accessibilityAPI = new Accessibility();
    accessibilityAPI.initialize();

    // anchor init
    const $$anchor = $content.querySelectorAll('a[href^="#"], a[href*="#"]');
    for (let i = 0; i < $$anchor.length; i++) {
        const $anchor = $$anchor[i];

        if ($anchor.getAttribute('data-tabs') !== 'trigger') {
            const anchorHref = $anchor.getAttribute('href');
            const anchorParts = anchorHref.split('//');
            const anchorPath = anchorParts[0];
            let internal = false;
            let samePage = false;

            if (anchorHref === anchorPath) {
                internal = true;

                if (anchorPath.indexOf('#') === 0) {
                    samePage = true;
                }
            }

            if (internal === samePage === true) {
                $anchor.addEventListener('click', (e) => {
                    const id = anchorHref.split('#')[1];
                    const $anchorTarget = document.getElementById(id);

                    if ($anchorTarget !== null) {
                        const anchorDimensions = getOffset($anchorTarget);
                        const anchorMovement = anchorDimensions.top;
                        smoothScrollTo(0, anchorMovement);
                    } else {
                        smoothScrollTo(0, window.innerHeight, 1000);
                    }

                    e.preventDefault();
                });
            }
        }
    }

    // init rte tables
    const $rteTables = $content.querySelectorAll('.rte table');
    if ($rteTables.length > 0) {
        import('./components/rte/rte-table').then(({ RteTable }) => {
            for (let i = 0; i < $rteTables.length; i++) {
                const rteTable = new RteTable($rteTables[i]);
                rteTable.initialize();
            }
        });
    }
};

/**
 * starting point
 */

domready(() => {
    const initApplication = () => {
        initContent(document);

        window.eventScroller.init();
        window.resizeHandler.init();
        window.initContent = initContent;

        const contentUpdateEvent = new CustomEvent('content.loaded', {
            view: window,
            bubbles: true,
            cancelable: false,
            detail: {
                $context: document
            }
        });

        const $$cookieConsentOverlayOpenerLinks = document.querySelectorAll('a[href="cookie-consent"]');

        /**
         * Replaces each cookie consent anchor link with a semantic button element
         * so the cookie settings overlay can be triggered as an action instead of navigation.
         *
         * @param {HTMLAnchorElement} $link - The original anchor element to convert into a button.
         * @returns {void} Does not return a value.
         */
        $$cookieConsentOverlayOpenerLinks.forEach($link => {
            const $button = document.createElement('button');
            $button.textContent = $link.textContent;

            if ($link.classList.length > 0) {
                $button.className = $link.className;
            }

            /**
             * Copies only the relevant attributes from the original link to the replacement
             * button to preserve accessibility metadata and selector hooks.
             *
             * @param {Attr} attr - The current attribute from the original link element.
             * @returns {void} Does not return a value.
             */
            Array.from($link.attributes).forEach(attr => {
                // Preserve only the attributes required for identification, accessibility,
                // or cookie consent behavior on the generated button.
                if (attr.name === 'data-cookieconsent' || ['id', 'title', 'aria-label'].includes(attr.name)) {
                    $button.setAttribute(attr.name, attr.value);
                }
            });

            $button.setAttribute('data-cookieconsent', '');
            $button.setAttribute('type', 'button');

            // Replace the link with a button so the DOM reflects the actual UI behavior.
            $link.parentNode.replaceChild($button, $link);
        });

        const $$cookieConsentOverlayOpener = document.querySelectorAll('[data-cookieconsent]');

        /**
         * Attaches a click handler to each cookie consent trigger element so the OneTrust
         * cookie settings overlay can be opened on user interaction.
         *
         * @param {HTMLElement} $button - The element that should open the cookie consent overlay when clicked.
         * @returns {void} Does not return a value.
         */
        $$cookieConsentOverlayOpener.forEach($button => {
            /**
             * Opens the OneTrust cookie settings overlay when the trigger element is clicked.
             *
             * @param {MouseEvent} e - The click event triggered by the element.
             * @returns {void} Does not return a value.
             */
            $button.addEventListener('click', e => {
                // Guard against cases where the OneTrust script is not available yet.
                if (typeof OneTrust !== 'undefined') {
                    OneTrust.ToggleInfoDisplay();
                }

                // Prevent default interaction behavior so only the overlay action is executed.
                e.preventDefault();
            });
        });

        window.dispatchEvent(contentUpdateEvent);

        // Remove no Transitions for all Elements at Start
        setTimeout(() => {
            document.body.classList.remove('has--no-animation');
        }, 1000);

        const $bannerSelector = document.querySelector('[data-smartbanner]');
        if ($bannerSelector) {
            AdjustSmartBanner.init({
                appToken: 'na72bqc48ow0',
                language: 'de',
                logLevel: 'verbose'
            });
        }
    };

    const handleOneTrust = ($links) => {
        $links.forEach($link => {
            $link.addEventListener('click', (e) => {
                if (typeof OneTrust !== 'undefined') {
                    OneTrust.ToggleInfoDisplay();
                }
                e.preventDefault();
            });
        });
    };

    handleOneTrust(document.querySelectorAll('[rel="onetrust-link"]'));

    if (!window.Modernizr.ie11) {
        initApplication();
    }
});
